// Shared constants + helpers for the Accidental Vehicles module.
// Keeping them in one place so dashboard/workbench/detail stay consistent.

export const AV_STATUSES = [
  { value: "vehicle_received", label: "Vehicle Received" },
  { value: "estimate_pending", label: "Estimate Pending" },
  { value: "customer_approval_pending", label: "Customer Approval Pending" },
  { value: "documents_pending", label: "Documents Pending" },
  { value: "parts_pending", label: "Parts Pending" },
  { value: "repair_in_progress", label: "Repair In Progress" },
  { value: "billing_pending", label: "Billing Pending" },
  { value: "ready_delivery", label: "Customer Pickup Pending" },
  { value: "delivered", label: "Delivered" },
  { value: "on_hold", label: "On Hold" },
  { value: "cancelled", label: "Cancelled" },
];

export const AV_STATUS_TONES = {
  vehicle_received: "bg-yamaha-50 text-yamaha-700 border-yamaha-200",
  estimate_pending: "bg-amber-50 text-amber-700 border-amber-200",
  customer_approval_pending: "bg-amber-50 text-amber-700 border-amber-200",
  documents_pending: "bg-amber-50 text-amber-700 border-amber-200",
  parts_pending: "bg-violet-50 text-violet-700 border-violet-200",
  repair_in_progress: "bg-yamaha-50 text-yamaha-700 border-yamaha-200",
  billing_pending: "bg-amber-50 text-amber-700 border-amber-200",
  ready_delivery: "bg-emerald-50 text-emerald-700 border-emerald-200",
  delivered: "bg-emerald-50 text-emerald-700 border-emerald-200",
  on_hold: "bg-rose-50 text-rose-700 border-rose-200",
  cancelled: "bg-[#f4f3fc] text-[#444653] border-[#e2e1eb]",
  draft: "bg-[#f4f3fc] text-[#444653] border-[#e2e1eb]",
};

export const statusLabel = (s) =>
  (AV_STATUSES.find((x) => x.value === s) || { label: (s || "—").replace(/_/g, " ") }).label;

// Ageing — colour treatments derived from the bucket the backend computes
// (so the rules live in one place: <=7 green, 8-15 yellow, >15 red).
export const AGEING_TONES = {
  green: {
    text: "text-emerald-700",
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    dot: "bg-emerald-500",
  },
  yellow: {
    text: "text-amber-700",
    bg: "bg-amber-50",
    border: "border-amber-200",
    dot: "bg-amber-500",
  },
  red: {
    text: "text-rose-700",
    bg: "bg-rose-50",
    border: "border-rose-200",
    dot: "bg-rose-500",
  },
};

export const HOLD_REASONS = [
  "Customer Approval Pending",
  "Customer Documents Pending",
  "Insurance Survey Pending",
  "Parts Not Available",
  "Customer Not Responding",
  "Workshop Capacity",
  "Disputed Case",
  "Other",
];

export const CLAIM_TYPES = [
  { value: "insured", label: "Insured" },
  { value: "non_insured", label: "Non Insured" },
];

// Roles that can create / edit core AV fields. Visibility is location-based
// (enforced by the backend).
export const AV_EDIT_ROLES = new Set([
  "admin",
  "floor_supervisor",
]);

export const canEditAv = (user) =>
  !!user && AV_EDIT_ROLES.has(user.role);

export const canHoldOrDeliverAv = (user) =>
  !!user && (user.role === "admin" || user.role === "floor_supervisor" || user.role === "works_manager");

// Per-tab role gates (UI hides controls; backend enforces same rules).
export const canEditEstimate = (user) =>
  !!user && (user.role === "admin" || user.role === "floor_supervisor");
export const canEditDocuments = (user) =>
  !!user && (user.role === "admin" || user.role === "floor_supervisor");
export const canEditParts = (user) =>
  !!user && (user.role === "admin" || user.role === "parts_manager");
export const canEditRepair = (user) =>
  !!user && (user.role === "admin" || user.role === "works_manager" || user.role === "floor_supervisor");
export const canEditBilling = (user) =>
  !!user && (user.role === "admin" || user.role === "billing_team" || user.role === "floor_supervisor");
export const canAddCrmNote = (user) =>
  !!user && (user.role === "admin" || user.role === "crm");
export const canDeleteCrmNote = (user) =>
  !!user && user.role === "admin";

export const ADVANCE_MODES = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "card", label: "Card" },
  { value: "bank_transfer", label: "Bank Transfer" },
];
export const advanceModeLabel = (m) =>
  (ADVANCE_MODES.find((x) => x.value === m) || { label: (m || "—").replace(/_/g, " ") }).label;

export const PAYMENT_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "partially_paid", label: "Partially Paid" },
  { value: "paid", label: "Paid" },
];
export const paymentStatusLabel = (s) =>
  (PAYMENT_STATUSES.find((x) => x.value === s) || { label: (s || "—").replace(/_/g, " ") }).label;

// Document slots — fixed list per the spec.
export const DOCUMENT_KINDS = [
  { kind: "doc_rc", label: "RC" },
  { kind: "doc_insurance", label: "Insurance Copy" },
  { kind: "doc_claim_form", label: "Claim Form" },
  { kind: "doc_driving_license", label: "Driving License" },
  { kind: "doc_station_diary", label: "Station Diary" },
  { kind: "doc_aadhaar", label: "Aadhaar" },
  { kind: "doc_other_1", label: "Other Document 1" },
  { kind: "doc_other_2", label: "Other Document 2" },
];

export const PARTS_STATUSES = [
  { value: "not_ordered", label: "Not Ordered" },
  { value: "ordered", label: "Ordered" },
  { value: "partially_received", label: "Partially Received" },
  { value: "received", label: "Received" },
  { value: "delayed", label: "Delayed" },
];

export const REPAIR_STATUSES = [
  { value: "not_started", label: "Not Started" },
  { value: "waiting_surveyor", label: "Waiting for Surveyor" },
  { value: "work_started", label: "Work Started" },
  { value: "denting", label: "Denting" },
  { value: "painting", label: "Painting" },
  { value: "assembly", label: "Assembly" },
  { value: "qc", label: "QC" },
  { value: "completed", label: "Completed" },
];

export const partsStatusLabel = (s) =>
  (PARTS_STATUSES.find((x) => x.value === s) || { label: (s || "—").replace(/_/g, " ") }).label;
export const repairStatusLabel = (s) =>
  (REPAIR_STATUSES.find((x) => x.value === s) || { label: (s || "—").replace(/_/g, " ") }).label;

// Friendly action → label map for the Timeline tab. Anything not in the
// map is shown as a humanised version of the action enum.
export const TIMELINE_ACTION_LABELS = {
  AV_CREATED: "AV Created",
  AV_UPDATED: "AV Updated",
  AV_RECEIVED_ON_SET: "Received On Updated",
  AV_HOLD_STARTED: "Hold Started",
  AV_HOLD_RESUMED: "Hold Resumed",
  AV_DELIVERED: "Delivered",
  AV_ATTACHMENT_ADDED: "Photo Uploaded",
  AV_ATTACHMENT_REMOVED: "Photo Removed",
  AV_ESTIMATE_ADDED: "Estimate Uploaded",
  AV_ESTIMATE_APPROVED: "Estimate Approved",
  AV_ESTIMATE_REJECTED: "Estimate Rejected",
  AV_ESTIMATE_PDF_UPLOADED: "Estimate PDF Uploaded",
  AV_PARTS_UPDATED: "Parts Status Updated",
  AV_PARTS_ITEM_ADDED: "Part Added",
  AV_PARTS_ITEM_UPDATED: "Part Updated",
  AV_PARTS_ITEM_REMOVED: "Part Removed",
  AV_REPAIR_UPDATED: "Repair Status Updated",
  AV_DOCUMENT_UPLOADED: "Document Uploaded",
  AV_INVOICE_PDF_UPLOADED: "Invoice PDF Uploaded",
  AV_ADVANCE_ADDED: "Advance Added",
  AV_ADVANCE_REMOVED: "Advance Removed",
  AV_BILLING_UPDATED: "Billing Updated",
  AV_CRM_NOTE_ADDED: "CRM Note Added",
  AV_CRM_NOTE_REMOVED: "CRM Note Removed",
};

export const timelineActionLabel = (a) =>
  TIMELINE_ACTION_LABELS[a] || (a || "—").replace(/^AV_/, "").replace(/_/g, " ");
